import React from 'react';
import { ArrowDownLeft, ArrowUpRight, Check } from 'lucide-react';
import { CURRENCY, formatAmount, toNumber } from '../shared/orderFormat';

/*
 * The gap between what the order is now worth and what was actually paid.
 *
 * Editing an order never rewrites the paid figures — the customer was charged
 * what they were charged, and that record has to survive. `balance_due` is the
 * difference, and it is the single most important number on an edited order:
 *
 *   positive  the customer owes this much
 *   negative  this much is owed back to them
 *   zero      settled
 *
 * Rendered as a distinct block rather than another row in the totals ladder,
 * because it isn't part of the sum — the ladder adds up to what the order costs
 * now, and this says what that means for money that has already moved.
 *
 * Nothing renders when it's zero or absent: a settled order shouldn't carry a
 * reassurance banner about a problem it doesn't have.
 */
const BalanceDue = ({ value }) => {
  const balance = toNumber(value);
  if (balance == null || balance === 0) return null;

  const owedByCustomer = balance > 0;
  const amount = formatAmount(Math.abs(balance));

  const tone = owedByCustomer
    ? {
        wrap: 'border-[#E3CE95] bg-[#FBF3DF]',
        icon: 'bg-white border-[#E3CE95] text-[#8A6508]',
        title: 'text-[#7A5A07]',
        body: 'text-[#8A6508]',
        Icon: ArrowUpRight,
      }
    : {
        wrap: 'border-[#BDD3DC] bg-[#EDF4F7]',
        icon: 'bg-white border-[#BDD3DC] text-[#33586A]',
        title: 'text-[#2B4E5F]',
        body: 'text-[#33586A]',
        Icon: ArrowDownLeft,
      };

  const { Icon } = tone;

  return (
    <div
      className={`mt-3 pt-3 border-t border-[#E1E7EA] flex items-start gap-2.5`}
    >
      <span
        className={`flex items-center justify-center w-7 h-7 rounded-lg border shrink-0 ${tone.icon}`}
        aria-hidden="true"
      >
        <Icon className="w-3.5 h-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className={`m-0 text-[13px] font-bold ${tone.title}`}>
          {owedByCustomer
            ? `Customer owes ${CURRENCY} ${amount}`
            : `Refund ${CURRENCY} ${amount} to the customer`}
        </p>
        <p className={`m-0 text-[11.5px] font-medium mt-0.5 ${tone.body}`}>
          {owedByCustomer
            ? 'The order is now worth more than was paid.'
            : 'The order is now worth less than was paid.'}
        </p>
      </div>
    </div>
  );
};

/** Shown once an edited order is settled again, so the change is acknowledged. */
export const BalanceSettled = () => (
  <div className="mt-3 pt-3 border-t border-[#E1E7EA] flex items-center gap-2 text-[11.5px] font-semibold text-[#256B4C]">
    <Check className="w-3.5 h-3.5" aria-hidden="true" />
    Paid in full — nothing outstanding.
  </div>
);

export default BalanceDue;
